import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Distributor } from '../../types/index.js';
import { Plus, Trash2, Star } from 'lucide-react';
import { useDistributors } from '../../hooks/useOfflineData.js';

interface DistributorLink {
  distributor_id: string;
  pos_number_at_distributor: string;
  is_primary: boolean;
}

interface DistributorSelectorProps {
  value?: DistributorLink[];
  onChange: (value: DistributorLink[]) => void;
}

export const DistributorSelector: React.FC<DistributorSelectorProps> = ({ value = [], onChange }) => {
  const [selectedLinks, setSelectedLinks] = useState<DistributorLink[]>(value);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLink, setNewLink] = useState<DistributorLink>({
    distributor_id: '',
    pos_number_at_distributor: '',
    is_primary: false,
  });

  // Use offline-first distributors hook
  const { distributors } = useDistributors();

  useEffect(() => {
    setSelectedLinks(value);
  }, [value]);

  const handleAddLink = () => {
    if (!newLink.distributor_id) {
      toast.warning('Please select a distributor');
      return;
    }

    // If this is marked as primary, unmark others
    const updatedLinks = newLink.is_primary
      ? selectedLinks.map(link => ({ ...link, is_primary: false }))
      : selectedLinks;

    const newLinks = [...updatedLinks, newLink];
    setSelectedLinks(newLinks);
    onChange(newLinks);
    
    // Reset form
    setNewLink({
      distributor_id: '',
      pos_number_at_distributor: '',
      is_primary: false,
    });
    setShowAddForm(false);
  };

  const handleRemoveLink = (index: number) => {
    const newLinks = selectedLinks.filter((_, i) => i !== index);
    setSelectedLinks(newLinks);
    onChange(newLinks);
  };

  const handleSetPrimary = (index: number) => {
    const newLinks = selectedLinks.map((link, i) => ({
      ...link,
      is_primary: i === index,
    }));
    setSelectedLinks(newLinks);
    onChange(newLinks);
  };

  const handleUpdatePosNumber = (index: number, posNumber: string) => {
    const newLinks = selectedLinks.map((link, i) =>
      i === index ? { ...link, pos_number_at_distributor: posNumber } : link
    );
    setSelectedLinks(newLinks);
    onChange(newLinks);
  };

  const getDistributorName = (id: string) => {
    return distributors.find(d => d.id === id)?.name || 'Unknown';
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <label className="block text-sm font-semibold text-gray-700">
          Distributors
        </label>
        {!showAddForm && (
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            <Plus size={16} />
            Distributor Add
          </button>
        )}
      </div>

      {/* Existing Links */}
      {selectedLinks.length > 0 && (
        <div className="space-y-2">
          {selectedLinks.map((link, index) => (
            <div
              key={index}
              className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-gray-50"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-gray-900">{getDistributorName(link.distributor_id)}</p>
                  {link.is_primary && (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                      <Star size={12} fill="currentColor" />
                      Primary
                    </span>
                  )}
                </div>
                <div className="mt-1">
                  <label className="block text-xs font-medium text-gray-500 mb-0.5">
                    PoS Number at Distributor
                  </label>
                  <input
                    type="text"
                    value={link.pos_number_at_distributor}
                    onChange={(e) => handleUpdatePosNumber(index, e.target.value)}
                    className="w-full px-2 py-1 text-sm font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    placeholder="e.g. K-123456"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {!link.is_primary && (
                  <button
                    type="button"
                    onClick={() => handleSetPrimary(index)}
                    className="px-2 py-1 text-xs text-gray-600 hover:text-yellow-600 transition-colors"
                    title="Set as primary distributor"
                  >
                    <Star size={16} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleRemoveLink(index)}
                  className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add New Link Form */}
      {showAddForm && (
        <div className="p-4 border-2 border-blue-200 rounded-lg bg-blue-50 space-y-3">
          <h4 className="font-semibold text-gray-900">Add New Distributor</h4>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Distributor <span className="text-red-500">*</span>
            </label>
            <select
              value={newLink.distributor_id}
              onChange={(e) => setNewLink({ ...newLink, distributor_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            >
              <option value="">-- Select Distributor --</option>
              {distributors
                .filter(d => !selectedLinks.some(link => link.distributor_id === d.id))
                .map(dist => (
                  <option key={dist.id} value={dist.id}>
                    {dist.name}
                  </option>
                ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              PoS Number at Distributor <span className="text-gray-400 text-xs">(optional)</span>
            </label>
            <input
              type="text"
              value={newLink.pos_number_at_distributor}
              onChange={(e) => setNewLink({ ...newLink, pos_number_at_distributor: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              placeholder="e.g. K-123456 (if known)"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is-primary"
              checked={newLink.is_primary}
              onChange={(e) => setNewLink({ ...newLink, is_primary: e.target.checked })}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="is-primary" className="text-sm text-gray-700 cursor-pointer">
              Mark as primary distributor
            </label>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleAddLink}
              className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAddForm(false);
                setNewLink({ distributor_id: '', pos_number_at_distributor: '', is_primary: false });
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {selectedLinks.length === 0 && !showAddForm && (
        <p className="text-sm text-gray-500 italic">
          No distributors added. Click "Distributor Add" to link a distributor.
        </p>
      )}
    </div>
  );
};

